import type { Choice, ResourceDescriptor } from './descriptors';

/**
 * Catalogue des ressources reellement servies par l'API.
 *
 * Chaque entree a ete relevee dans le controleur correspondant du backend, pas
 * dans une documentation : les operations declarees ici sont celles qui existent
 * dans `backend/src/<Contexte>/Presentation/Http`. Les listes de choix
 * reproduisent les enums PHP du domaine — une valeur inventee serait refusee en
 * `validation_failed` a l'enregistrement.
 *
 * Ce que le catalogue **ne fait pas** : promettre une operation absente.
 *
 * **Il ne contient plus les ressources portees par un profil bebe** (lieux, lots,
 * tirages, seances d'alimentation, mesures de croissance, aliments, mouvements
 * de stock) : elles lisaient `/babies/{babyId}/…`, des tables que les telephones
 * n'alimentent pas. L'explorateur de donnees (`admin/data/`) les remplace en lisant
 * le journal de synchronisation (ADR 0035 §1). Le code generique qui les servait
 * (chemin `{babyId}`, pagination serveur…) reste en place, exerce par les
 * descripteurs de `testing/descriptorFixtures.ts`.
 */

/**
 * Valeurs de `BabyProfile::normalizeSex` (serveur) ; un test les compare au code
 * PHP. « Non précisé » n'est pas une valeur : c'est l'absence de valeur (`null`),
 * proposée par l'option vide de la liste (voir `inputs.tsx`).
 */
export const SEXES: readonly Choice[] = [
  { id: 'female', name: 'Fille' },
  { id: 'male', name: 'Garçon' },
  { id: 'other', name: 'Autre' },
];

/**
 * Champs communs a tout enregistrement synchronisable, toujours calcules par le
 * serveur. Les repeter dans chaque descripteur serait du bruit.
 */
const auditFields = [
  {
    name: 'version',
    label: 'Version',
    kind: 'integer' as const,
    writability: 'readOnly' as const,
    helperText: 'Compteur de synchronisation, incrémenté par le serveur.',
  },
  {
    name: 'createdAt',
    label: 'Créé le',
    kind: 'dateTime' as const,
    writability: 'readOnly' as const,
  },
  {
    name: 'updatedAt',
    label: 'Modifié le',
    kind: 'dateTime' as const,
    writability: 'readOnly' as const,
  },
];

const archived = (record: Readonly<Record<string, unknown>>): boolean => record.archived === true;
const notArchived = (record: Readonly<Record<string, unknown>>): boolean => !archived(record);

export const BABIES: ResourceDescriptor = {
  name: 'babies',
  label: 'Profils bébé',
  group: 'identite',
  path: '/babies',
  capabilities: { list: true, show: true, create: true, edit: true },
  singleRecord: 'endpoint',
  pagination: 'client',
  serverFilters: [],
  actions: [
    {
      name: 'archive',
      label: 'Archiver',
      suffix: 'archive',
      method: 'POST',
      confirmation:
        "Archiver ce profil le retire des écrans de suivi sans supprimer aucune donnée. L'opération est réversible.",
      appliesTo: notArchived,
    },
    {
      name: 'restore',
      label: 'Restaurer',
      suffix: 'restore',
      method: 'POST',
      appliesTo: archived,
    },
  ],
  notice:
    "Cette liste ne contient que les profils auxquels le compte connecté a accès : le serveur filtre sur l'appartenance, jamais le backoffice.",
  fields: [
    { name: 'id', label: 'Identifiant', kind: 'identifier', writability: 'readOnly' },
    { name: 'firstName', label: 'Prénom', kind: 'text', writability: 'always', required: true, listed: true },
    { name: 'lastName', label: 'Nom', kind: 'text', writability: 'always', listed: true },
    {
      name: 'birthDate',
      label: 'Date de naissance',
      kind: 'date',
      writability: 'always',
      required: true,
      listed: true,
    },
    { name: 'sex', label: 'Sexe', kind: 'choice', writability: 'always', choices: SEXES, listed: true },
    {
      name: 'birthWeightGrams',
      label: 'Poids de naissance',
      kind: 'grams',
      writability: 'always',
      helperText: 'En grammes.',
    },
    {
      name: 'birthHeightMillimeters',
      label: 'Taille de naissance',
      kind: 'millimeters',
      writability: 'always',
      helperText: 'En millimètres.',
    },
    {
      name: 'birthHeadCircumferenceMillimeters',
      label: 'Périmètre crânien de naissance',
      kind: 'millimeters',
      writability: 'always',
      helperText: 'En millimètres.',
    },
    {
      name: 'dueDate',
      label: 'Date prévue d’accouchement',
      kind: 'date',
      writability: 'always',
      helperText: 'Sert au calcul de l’âge corrigé pour un bébé prématuré.',
    },
    { name: 'note', label: 'Note', kind: 'longText', writability: 'always' },
    { name: 'archived', label: 'Archivé', kind: 'boolean', writability: 'readOnly', listed: true },
    { name: 'archivedAt', label: 'Archivé le', kind: 'dateTime', writability: 'readOnly' },
    ...auditFields,
  ],
};

/** Catalogue complet, dans l'ordre d'affichage du menu. */
export const RESOURCE_CATALOG: readonly ResourceDescriptor[] = [BABIES];

/** Libelles des groupes du menu. */
export const GROUP_LABELS: Readonly<Record<string, string>> = {
  identite: 'Identité',
  stock: 'Stock',
  suivi: 'Suivi',
  audit: 'Audit',
  legal: 'Mentions légales',
};

export const findDescriptor = (name: string): ResourceDescriptor | undefined =>
  RESOURCE_CATALOG.find((descriptor) => descriptor.name === name);
